import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import { userService } from "../../../services/userService";
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api from "../../../services/api";
import { API_ENDPOINTS } from "../../../utils/constants";
import { useRealtimePrices } from "../../../hooks/useRealtimePrices";
import { subscribeSymbols, unsubscribeDelayed } from "../../../ws/marketSubscriptions";
import "./UserDetailsPage.css";
import { formatISTDateTime, formatISTDate } from '../../../utils/datetime';

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);



const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const parseJsonField = (field) => {
  if (!field) return [];
  if (Array.isArray(field)) return field;
  
  if (typeof field === "string") {
    try {
      const parsed = JSON.parse(field);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "object" && parsed !== null) {
        const values = Object.values(parsed);
        if (values.length === 1 && Array.isArray(values[0])) return values[0];
        return [parsed];
      }
      return [];
    } catch {
      return field.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }

  if (typeof field === "object" && field !== null) {
    const values = Object.values(field);
    if (values.length === 1 && Array.isArray(values[0])) return values[0];
    return [field];
  }

  return [];
};

/* ─── Detail Item Component ───────────────────── */
const DetailItem = ({ label, value, mono, full, children }) => (
  <div className={`ud-detail-item${full ? " full" : ""}`}>
    <span className="ud-detail-label">{label}</span>
    {children ? (
      children
    ) : (
      <span className={`ud-detail-value${mono ? " mono" : ""}`}>
        {value || "—"}
      </span>
    )}
  </div>
);

/* ─── Badge Component ─────────────────────────── */
const Badge = ({ type, children }) => (
  <span className={`ud-badge ${type}`}>{children}</span>
);

/* ─── Section Component ───────────────────────── */
const Section = ({ title, icon, iconColor = "purple", fullWidth, headerRight, children }) => (
  <div className={`ud-section${fullWidth ? " full-width" : ""}`}>
    <div className="ud-section-header">
      <div className={`ud-section-icon ${iconColor}`}>
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <h3 className="ud-section-title">{title}</h3>
      {headerRight && <div className="ud-section-header-right">{headerRight}</div>}
    </div>
    <div className="ud-section-body">{children}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════ */
const UserDetailsPage = () => {
  const { prices } = useRealtimePrices();
  const [searchId, setSearchId] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [userWatchlist, setUserWatchlist] = useState([]);
  const [userPortfolio, setUserPortfolio] = useState([]);
  const [watchlistPage, setWatchlistPage] = useState(1);
  const [portfolioPage, setPortfolioPage] = useState(1);
  const itemsPerPage = 5;
  const [scriptsData, setScriptsData] = useState([]);
  const [brokersMap, setBrokersMap] = useState({});

  // Fetch all scripts for symbol lookup and mapping
  useEffect(() => {
    const fetchScripts = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.SCRIPT);
        if (response?.data) {
          const scriptsArray = response.data.data || response.data || [];
          setScriptsData(Array.isArray(scriptsArray) ? scriptsArray : []);
        }
      } catch (error) {
        console.error("Failed to fetch scripts:", error);
      }
    };
    fetchScripts();
  }, []);

  // Fetch broker master for broker name lookup (with hardcoded fallback)
  useEffect(() => {
    // Default broker map so it works even if the API is unavailable
    const defaultMap = { 1: 'AngelOne' };
    const fetchBrokers = async () => {
      try {
        const response = await api.get('/portfolio/getAllBrokers');
        const brokers = response?.data?.data || response?.data || [];
        if (Array.isArray(brokers) && brokers.length > 0) {
          const map = {};
          brokers.forEach(b => { map[b.id] = b.name || b.broker_name || `Broker ${b.id}`; });
          setBrokersMap(map);
          return;
        }
      } catch (_) {
        // Silently fall back to defaults
      }
      setBrokersMap(defaultMap);
    };
    fetchBrokers();
  }, []);

  // Helper: find matching script from scriptsData for a given portfolio/watchlist item
  const findMatchedScript = useCallback((item) => {
    if (!Array.isArray(scriptsData) || scriptsData.length === 0) return null;
    if (typeof item !== 'object') return null;
    const idToMatch = item.script_id || item.scriptId || item.token || item.symboltoken || item.symbol || item.tradingsymbol || item.id || item.script_name || item.name;
    if (!idToMatch) return null;
    return scriptsData.find(s => {
      const scriptSym = (s.symbol || s.script_name || '').toUpperCase().trim();
      const itemSym = String(idToMatch).toUpperCase().trim();
      return String(s.script_id) === String(idToMatch) ||
             String(s.token) === String(idToMatch) ||
             String(s.id) === String(idToMatch) ||
             scriptSym === itemSym;
    }) || null;
  }, [scriptsData]);

  // Subscribe watchlist & portfolio tokens for live LTP via WebSocket
  const tokensToSubscribe = useMemo(() => {
    const tokens = new Set();
    userWatchlist.forEach(item => {
      if (typeof item === 'object') {
        const matched = findMatchedScript(item);
        const sym = matched?.symbol || item.script_symbol || item.symbol || item.tradingsymbol;
        if (sym) tokens.add(sym.toUpperCase().trim());
      }
    });
    userPortfolio.forEach(item => {
      if (typeof item === 'object') {
        const matched = findMatchedScript(item);
        const sym = matched?.symbol || item.tradingsymbol || item.symbol;
        if (sym) tokens.add(sym.toUpperCase().trim());
      }
    });
    return [...tokens];
  }, [userWatchlist, userPortfolio, findMatchedScript]);

  useEffect(() => {
    if (tokensToSubscribe.length === 0) return;
    const page = "UserDetails";
    const context = "WatchlistPortfolio";
    subscribeSymbols(tokensToSubscribe, page, context);
    return () => {
      unsubscribeDelayed(tokensToSubscribe, page, context);
    };
  }, [tokensToSubscribe]);

  // Crop states
  const [upImg, setUpImg] = useState();
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);


  const handleSearch = useCallback(async () => {
    const trimmed = searchId.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setUser(null);
    setUserWatchlist([]);
    setUserPortfolio([]);
    setWatchlistPage(1);
    setPortfolioPage(1);
    setSearched(true);
    setWatchlistPage(1);
    setPortfolioPage(1);

    try {
      let userIdToFetch = trimmed;

      // Try searching first to resolve username or phone number to an ID
      try {
        const searchRes = await api.get(`/users`, { params: { search: trimmed } });
        const usersList = searchRes?.data?.data || searchRes?.data || [];
        if (Array.isArray(usersList) && usersList.length > 0) {
          const exactMatch = usersList.find(u => 
            String(u.userid) === trimmed || 
            String(u.username) === trimmed || 
            String(u.phone) === trimmed ||
            (u.name && String(u.name).toLowerCase() === trimmed.toLowerCase())
          );
          if (exactMatch) {
            userIdToFetch = exactMatch.userid;
          }
        }
      } catch (searchErr) {
        console.warn("Search API failed, falling back to direct ID fetch:", searchErr);
      }

      const response = await api.get(`/users/${userIdToFetch}`);
      const userData = response?.data || response;

      if (!userData || (typeof userData === "object" && Object.keys(userData).length === 0)) {
        setError("No user found with this ID, username, phone number, or name.");
        setUser(null);
        setUserWatchlist([]);
        setUserPortfolio([]);
      } else {
        setUser(userData);

        // Fetch Watchlist (two-step: get watchlist IDs, then get stocks for each)
        try {
          // Step 1: Get user's watchlist control table entries
          const wlControlRes = await api.get(`/wishlistcontrol?user_id=${trimmed}`);
          const watchlists = wlControlRes?.data || [];

          if (Array.isArray(watchlists) && watchlists.length > 0) {
            // Step 2: Get stocks for each watchlist and merge
            const allStocks = [];
            for (const wl of watchlists) {
              try {
                const stocksRes = await api.get(`/wishlistcontrol/stocks?wishlist_id=${wl.wishlist_id}`);
                const stocks = stocksRes?.data || [];
                if (Array.isArray(stocks)) {
                  allStocks.push(...stocks);
                }
              } catch (stockErr) {
                console.error(`Watchlist ${wl.wishlist_id} stocks fetch error:`, stockErr);
              }
            }
            setUserWatchlist(allStocks);
          } else {
            setUserWatchlist([]);
          }
        } catch (err) {
          console.error("Watchlist fetch error:", err);
          setUserWatchlist([]);
        }

        // Fetch Portfolio (uses getPortfolioBalance which reads userid from headers or query)
        try {
          const pfRes = await api.get(`/portfolio/getPortfolioBalance?userid=${trimmed}`, {
            headers: { userid: trimmed }
          });
          let pfData = pfRes?.data || [];
          setUserPortfolio(Array.isArray(pfData) ? pfData : []);
        } catch (err) {
          console.error("Portfolio fetch error:", err);
          setUserPortfolio([]);
        }
      }
    } catch (err) {
      const status = err?.status || err?.response?.status;
      const msg = (err?.message || "").toLowerCase();
      if (status === 404 || msg.includes("invalid") || msg.includes("suspicious")) {
        setError("No user found with this ID, username, phone number, or name.");
      } else {
        setError(err?.message || "Failed to fetch user details. Please try again.");
      }
      setUser(null);
      setUserWatchlist([]);
      setUserPortfolio([]);
    } finally {
      setLoading(false);
    }
  }, [searchId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClear = () => {
    setSearchId("");
    setUser(null);
    setUserWatchlist([]);
    setUserPortfolio([]);
    setError(null);
    setSearched(false);
    setWatchlistPage(1);
    setPortfolioPage(1);
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setUpImg(reader.result);
        setShowCropModal(true);
      });
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    imgRef.current = e.currentTarget;
    const cropConfig = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        1,
        width,
        height
      ),
      width,
      height
    );
    setCrop(cropConfig);
  };

  const getCroppedImgBase64 = () => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }
    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return canvas.toDataURL("image/jpeg", 0.9);
  };

  const handleCropSave = async () => {
    const base64Image = getCroppedImgBase64();
    if (!base64Image) return;

    try {
      setLoading(true);
      setShowCropModal(false);
      await userService.updateUser(user.userid, {
        userimage: base64Image
      });
      setUser({ ...user, userimage: base64Image });
      toast.success("Profile picture updated successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update profile picture");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!window.confirm("Are you sure you want to remove the profile picture?")) return;
    try {
      setLoading(true);
      await userService.updateUser(user.userid, {
        userimage: ""
      });
      setUser({ ...user, userimage: "" });
      toast.success("Profile picture removed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to remove profile picture");
    } finally {
      setLoading(false);
    }
  };

  // Refresh portfolio handler
  const [portfolioRefreshing, setPortfolioRefreshing] = useState(false);
  const handleRefreshPortfolio = useCallback(async () => {
    const trimmed = searchId.trim();
    if (!trimmed) return;
    setPortfolioRefreshing(true);
    try {
      const pfRes = await api.get(`/portfolio/getPortfolioBalance?userid=${trimmed}`, {
        headers: { userid: trimmed }
      });
      let pfData = pfRes?.data || [];
      setUserPortfolio(Array.isArray(pfData) ? pfData : []);
      setPortfolioPage(1);
      toast.success("Portfolio refreshed");
    } catch (err) {
      console.error("Portfolio refresh error:", err);
      toast.error("Failed to refresh portfolio");
    } finally {
      setPortfolioRefreshing(false);
    }
  }, [searchId]);

  // Parse user data
  const isBlocked = user ? parseInt(user.isblocked) === 1 : false;
  const isRA = user ? parseInt(user.isresearchanalyst) === 1 : false;
  const phoneVerified = user?.phoneverified;
  const emailVerified = user?.emailverified;
  const agreements = user ? parseJsonField(user.agreements) : [];
  const prefSectors = user ? parseJsonField(user.prefsectors) : [];
  const prefIndustries = user ? parseJsonField(user.prefindustries) : [];
  const prefStocks = user ? parseJsonField(user.prefstocks) : [];
  const preferencesOnApp = user ? parseJsonField(user.preferenceonapp) : [];
  const timelineContent = user ? parseJsonField(user.timelinecontent) : [];
  const permissions = user ? parseJsonField(user.permissions) : [];
  const watchlist = userWatchlist;
  const portfolio = userPortfolio;
  const paginatedWatchlist = watchlist.slice((watchlistPage - 1) * itemsPerPage, watchlistPage * itemsPerPage);
  const paginatedPortfolio = portfolio.slice((portfolioPage - 1) * itemsPerPage, portfolioPage * itemsPerPage);

  return (
    <div className="ud-page">
      {/* ─── Header ─────────────────────────── */}
      <div className="ud-header">
        <div className="ud-header-left">
          <h1>User Details</h1>
          <p>Look up a user by their User ID, Username, Phone Number, or Name to view full profile information</p>
        </div>
      </div>

      {/* ─── Search Bar ─────────────────────── */}
      <div className="ud-search-section">
        <div className="ud-search-wrap">
          <SearchIcon />
          <input
            type="text"
            placeholder="Enter User ID, Username, Phone, or Name"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            onKeyDown={handleKeyDown}
            id="user-detail-search"
          />
        </div>
        <button
          type="button"
          className="ud-btn-search"
          onClick={handleSearch}
          disabled={loading || !searchId.trim()}
          id="user-detail-search-btn"
        >
          {loading ? "Searching..." : "Search"}
        </button>
        {searched && (
          <button
            type="button"
            className="ud-btn-clear"
            onClick={handleClear}
            id="user-detail-clear-btn"
          >
            Clear
          </button>
        )}
      </div>

      {/* ─── Error Message ──────────────────── */}
      {error && (
        <div className="ud-error">
          <span className="material-symbols-outlined">error</span>
          <span className="ud-error-text">{error}</span>
          <button className="ud-error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* ─── Loading State ──────────────────── */}
      {loading && (
        <div className="ud-loading">
          <div className="ud-spinner" />
          <p>Fetching user details...</p>
        </div>
      )}

      {/* ─── Empty / Initial State ──────────── */}
      {!loading && !user && !error && (
        <div className="ud-empty-state">
          <div className="ud-empty-icon">
            <span className="material-symbols-outlined">person_search</span>
          </div>
          <h3>{searched ? "No Results Found" : "Search for a User"}</h3>
          <p>
            {searched
              ? "No user found with the provided ID, Username, Phone Number, or Name. Please check and try again."
              : "Enter a User ID, Username, Phone Number, or Name in the search bar above to view their complete profile, KYC status, plans, demat links, and more."}
          </p>
        </div>
      )}

      {/* ═══════════════════════════════════════
           User Profile Data
      ═══════════════════════════════════════ */}
      {user && (
        <>


          {/* ─── Crop Modal ─────────────────── */}
          {showCropModal && (
            <div className="ud-crop-modal-overlay">
              <div className="ud-crop-modal-content">
                <div className="ud-crop-header">
                  <h3>Adjust Profile Picture</h3>
                  <button className="ud-crop-close" onClick={() => setShowCropModal(false)}>×</button>
                </div>
                <div className="ud-crop-body">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                    circularCrop
                  >
                    <img src={upImg} onLoad={onImageLoad} alt="Crop" />
                  </ReactCrop>
                </div>
                <div className="ud-crop-footer">
                  <button className="ud-btn-cancel" onClick={() => setShowCropModal(false)}>Cancel</button>
                  <button className="ud-btn-save" onClick={handleCropSave}>Save Picture</button>
                </div>
              </div>
            </div>
          )}

          {/* ─── Section Grid ─────────────────── */}
          <div className="ud-sections">

            {/* ─── Contact & Basic Info ──────── */}
            <Section title="Contact Information" icon="contact_mail" iconColor="blue">
              <div className="ud-avatar-container">
                <div className="ud-avatar-upload-area">
                  <div className="ud-avatar-wrapper ud-avatar-lg">
                    {user.userimage ? (
                      <img
                        src={user.userimage.startsWith('http') || user.userimage.startsWith('data:image') ? user.userimage : `data:image/jpeg;base64,${user.userimage}`}
                        alt="Profile"
                        className="ud-avatar"
                      />
                    ) : (
                      <div className="ud-avatar">{getInitials(user.name)}</div>
                    )}
                    <label className="ud-avatar-upload-overlay" title="Upload Profile Picture">
                      <span className="material-symbols-outlined">photo_camera</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="ud-hidden-input"
                        onChange={onSelectFile}
                      />
                    </label>
                  </div>
                  {user.userimage && (
                    <button className="ud-btn-remove ud-btn-remove-sm" onClick={handleRemoveImage}>
                      Remove
                    </button>
                  )}
                </div>

                <div className="ud-detail-grid ud-flex-1">
                  <DetailItem label="Full Name" value={user.name} />
                  <DetailItem label="Username" value={user.username ? `@${user.username}` : null} />
                  <DetailItem label="Email" value={user.email} />
                  <DetailItem label="Phone" value={user.phone} />
                  <DetailItem label="User ID" value={user.userid} mono />
                  <DetailItem label="Status">
                    {isBlocked ? (
                      <Badge type="no">Blocked</Badge>
                    ) : (
                      <Badge type="yes">Active</Badge>
                    )}
                  </DetailItem>
                  <DetailItem label="Age" value={user.age} />
                  <DetailItem label="Gender" value={user.gender || user.genderid} />
                </div>
              </div>
            </Section>

            {/* ─── KYC & Verification ───────── */}
            <Section title="KYC & Verification" icon="verified_user" iconColor="green">
              <div className="ud-detail-grid">
                <DetailItem label="Phone Verified">
                  <Badge type={phoneVerified ? "yes" : "no"}>
                    {phoneVerified ? "Verified" : "Not Verified"}
                  </Badge>
                </DetailItem>
                <DetailItem label="Email Verified">
                  <Badge type={emailVerified ? "yes" : "no"}>
                    {emailVerified ? "Verified" : "Not Verified"}
                  </Badge>
                </DetailItem>
                <DetailItem label="PAN Number" value={user.pannumber} mono />
                <DetailItem label="Aadhaar Number" value={user.aadharnumber} mono />
                <DetailItem label="KYC Status">
                  <Badge type={user.kycstatus === "verified" ? "yes" : user.kycstatus === "pending" ? "pending" : "neutral"}>
                    {user.kycstatus || "Not Submitted"}
                  </Badge>
                </DetailItem>
                <DetailItem label="KYC Date" value={formatISTDate(user.kycdate)} />
              </div>
            </Section>

            {/* ─── Demat Account Links ──────── */}
            <Section title="Demat Account Links" icon="account_balance" iconColor="purple">
              {user.demataccounts && parseJsonField(user.demataccounts).length > 0 ? (
                <div className="ud-demat-list">
                  {parseJsonField(user.demataccounts).map((demat, idx) => (
                    <div className="ud-demat-card" key={idx}>
                      <div className="ud-demat-icon">
                        <span className="material-symbols-outlined">account_balance</span>
                      </div>
                      <div className="ud-demat-info">
                        <div className="ud-demat-broker">{demat.broker || demat.brokerName || demat.name || `Broker ${idx + 1}`}</div>
                        <div className="ud-demat-id">{demat.clientId || demat.accountId || demat.id || "—"}</div>
                      </div>
                      <div className="ud-demat-status">
                        <Badge type={demat.status === "active" || demat.isLinked ? "yes" : "neutral"}>
                          {demat.status === "active" || demat.isLinked ? "Linked" : demat.status || "Unknown"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : user.brokerClientId || user.brokerclientid ? (
                <div className="ud-demat-list">
                  <div className="ud-demat-card">
                    <div className="ud-demat-icon">
                      <span className="material-symbols-outlined">account_balance</span>
                    </div>
                    <div className="ud-demat-info">
                      <div className="ud-demat-broker">{user.brokerName || user.brokername || "Broker"}</div>
                      <div className="ud-demat-id">Client ID: {user.brokerClientId || user.brokerclientid}</div>
                    </div>
                    <div className="ud-demat-status">
                      <Badge type="info">Linked</Badge>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ud-no-data">No demat accounts linked</div>
              )}
            </Section>

            {/* ─── Subscription & Plans ─────── */}
            <Section title="Subscription & Plans" icon="star" iconColor="orange">
              {user.planName || user.planname || user.currentplan ? (
                <div className="ud-plan-card">
                  <div className="ud-plan-icon">
                    <span className="material-symbols-outlined">workspace_premium</span>
                  </div>
                  <div className="ud-plan-info">
                    <div className="ud-plan-name">{user.planName || user.planname || user.currentplan || "Active Plan"}</div>
                    <div className="ud-plan-validity">
                      Valid till: {formatISTDate(user.planExpiry || user.planexpiry || user.planvalidity)}
                    </div>
                  </div>
                  {(user.planPrice || user.planprice) && (
                    <div className="ud-plan-price">₹{user.planPrice || user.planprice}</div>
                  )}
                </div>
              ) : (
                <div className="ud-no-data">No active subscription</div>
              )}
              <div className="ud-detail-grid ud-mt-14">
                <DetailItem label="Plan Start Date" value={formatISTDate(user.planstartdate || user.planStartDate)} />
                <DetailItem label="Plan End Date" value={formatISTDate(user.planenddate || user.planEndDate || user.planexpiry)} />
                <DetailItem label="Payment Method" value={user.paymentmethod || user.paymentMethod} />
                <DetailItem label="Transaction ID" value={user.transactionid || user.transactionId} mono />
              </div>
            </Section>

            {/* ─── Research Analyst Info ─────── */}
            <Section title="Research Analyst" icon="analytics" iconColor="purple">
              <div className="ud-detail-grid">
                <DetailItem label="RA Status">
                  <Badge type={isRA ? "yes" : "neutral"}>{isRA ? "Active RA" : "Not an RA"}</Badge>
                </DetailItem>
                <DetailItem label="RA Number" value={user.ranumber} mono />
                <DetailItem label="RA Validity" value={formatISTDate(user.ravalidity)} />
                <DetailItem label="SEBI Registration" value={user.sebiregistration || user.sebinumber} mono />
              </div>

              {/* Agreements */}
              {agreements.length > 0 && (
                <>
                  <h4 className="ud-ra-heading">RA Agreements</h4>
                  <div className="ud-agreement-list">
                    {agreements.map((agreement, idx) => (
                      <div className="ud-agreement-item" key={idx}>
                        <span className="ud-agreement-name">
                          {agreement.agreementType || agreement.type || `Agreement ${idx + 1}`}
                        </span>
                        <Badge type={agreement.agreementStatus === "signed" ? "yes" : "pending"}>
                          {agreement.agreementStatus || agreement.status || "Pending"}
                        </Badge>
                        <span className="ud-agreement-date">
                          {formatISTDate(agreement.signedDate || agreement.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Section>

            {/* ─── Access & Permissions ─────── */}
            <Section title="Access & Permissions" icon="admin_panel_settings" iconColor="red">
              <div className="ud-detail-grid">
                <DetailItem label="Account Status">
                  <Badge type={isBlocked ? "no" : "yes"}>
                    {isBlocked ? "Blocked" : "Active"}
                  </Badge>
                </DetailItem>
                <DetailItem label="Blocking Note" value={user.blockingnote} />
              </div>
              {permissions.length > 0 && (
                <div className="ud-mt-14">
                  <span className="ud-detail-label">Assigned Roles</span>
                  <div className="ud-tags ud-mt-6">
                    {permissions.map((perm, idx) => (
                      <span className="ud-tag" key={idx}>{perm}</span>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* ─── Preferences & Interests ──── */}
            <Section title="Preferences & Interests" icon="tune" iconColor="blue" fullWidth>
              <div className="ud-detail-grid">
                <DetailItem label="Experience in Market" value={user.experienceinmarket} />
                <DetailItem label="Need Guidance" value={user.needguidance} />
              </div>

              {prefSectors.length > 0 && (
                <div className="ud-mt-14">
                  <span className="ud-detail-label">Preferred Sectors</span>
                  <div className="ud-tags ud-mt-6">
                    {prefSectors.map((s, idx) => <span className="ud-tag" key={idx}>{s}</span>)}
                  </div>
                </div>
              )}

              {prefIndustries.length > 0 && (
                <div className="ud-mt-14">
                  <span className="ud-detail-label">Preferred Industries</span>
                  <div className="ud-tags ud-mt-6">
                    {prefIndustries.map((i, idx) => <span className="ud-tag" key={idx}>{i}</span>)}
                  </div>
                </div>
              )}

              {prefStocks.length > 0 && (
                <div className="ud-mt-14">
                  <span className="ud-detail-label">Preferred Stocks</span>
                  <div className="ud-tags ud-mt-6">
                    {prefStocks.map((s, idx) => <span className="ud-tag" key={idx}>{s}</span>)}
                  </div>
                </div>
              )}

              {preferencesOnApp.length > 0 && (
                <div className="ud-mt-14">
                  <span className="ud-detail-label">App Preferences</span>
                  <div className="ud-tags ud-mt-6">
                    {preferencesOnApp.map((p, idx) => <span className="ud-tag" key={idx}>{p}</span>)}
                  </div>
                </div>
              )}

              {timelineContent.length > 0 && (
                <div className="ud-mt-14">
                  <span className="ud-detail-label">Timeline Content</span>
                  <div className="ud-tags ud-mt-6">
                    {timelineContent.map((t, idx) => <span className="ud-tag" key={idx}>{t}</span>)}
                  </div>
                </div>
              )}

              {prefSectors.length === 0 && prefIndustries.length === 0 && prefStocks.length === 0 && preferencesOnApp.length === 0 && timelineContent.length === 0 && !user.experienceinmarket && !user.needguidance && (
                <div className="ud-no-data">No preferences configured</div>
              )}
            </Section>

            {/* ─── Watchlist ──────────────────── */}
            <Section title="Watchlist" icon="visibility" iconColor="purple" fullWidth>
              {watchlist.length > 0 ? (
                <div className="ud-table-container">
                  <table className="ud-table">
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>LTP (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedWatchlist.map((item, idx) => {
                        const matchedScript = findMatchedScript(item);

                        // Use script_symbol (actual trading symbol) from the backend response
                        let symbol = "Unknown";
                        if (typeof item === 'object') {
                          symbol = item.script_symbol || item.symbol || item.tradingsymbol || (matchedScript?.symbol) || item.script_id || item.name || `Item ${idx + 1}`;
                        } else {
                          symbol = String(item);
                        }

                        // Use token directly from the backend response for live price lookup
                        const token = typeof item === 'object' ? (item.token || item.script_id || matchedScript?.token) : null;

                        let ltp = 0;
                        if (token && prices[token]?.price) {
                          ltp = prices[token].price;
                        } else if (typeof item === 'object' && (item.ltp || item.Close)) {
                          ltp = item.ltp || item.Close;
                        } else if (matchedScript?.ltp || matchedScript?.Close) {
                          ltp = matchedScript.ltp || matchedScript.Close;
                        }

                        return (
                          <tr key={idx}>
                            <td>{symbol}</td>
                            <td>{ltp ? Number(ltp).toFixed(2) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {watchlist.length > itemsPerPage && (
                    <div className="ud-pagination">
                      <button
                        onClick={() => setWatchlistPage(prev => Math.max(prev - 1, 1))}
                        disabled={watchlistPage === 1}
                      >
                        Previous
                      </button>
                      <span>Page {watchlistPage} of {Math.ceil(watchlist.length / itemsPerPage)}</span>
                      <button
                        onClick={() => setWatchlistPage(prev => Math.min(prev + 1, Math.ceil(watchlist.length / itemsPerPage)))}
                        disabled={watchlistPage === Math.ceil(watchlist.length / itemsPerPage)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="ud-no-data">No watchlist items</div>
              )}
            </Section>

            {/* ─── Portfolio ──────────────────── */}
            <Section
              title="Portfolio"
              icon="pie_chart"
              iconColor="blue"
              fullWidth
              headerRight={
                <button
                  className="ud-btn-refresh"
                  onClick={handleRefreshPortfolio}
                  disabled={portfolioRefreshing}
                  title="Refresh Portfolio"
                >
                  <span className={`material-symbols-outlined${portfolioRefreshing ? ' ud-spin-icon' : ''}`}>refresh</span>
                  {portfolioRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              }
            >
              {portfolio.length > 0 ? (
                <div className="ud-table-container">
                  <table className="ud-table">
                    <thead>
                      <tr>
                        <th>Asset Name</th>
                        <th>Broker</th>
                        <th>Invested Date</th>
                        <th>Quantity</th>
                        <th>LTP (₹)</th>
                        <th>Invested (₹)</th>
                        <th>Current Value (₹)</th>
                        <th>Gain/Loss (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPortfolio.map((item, idx) => {
                        const matchedScript = findMatchedScript(item);

                        // Portfolio data comes from portfolio_status table with fields:
                        // tradingsymbol, symboltoken, quantity, averageprice, ltp, close, etc.
                        let name = "Unknown Asset";
                        if (typeof item === 'object') {
                          name = item.tradingsymbol || item.script_name || item.symbol || (matchedScript?.script_name) || item.name || `Asset ${idx + 1}`;
                        } else {
                          name = String(item);
                        }

                        const quantity = typeof item === 'object' ? Number(item.quantity || item.qty || 0) : 0;
                        // DB column is 'averageprice' (no underscore)
                        const averagePrice = typeof item === 'object' ? Number(item.averageprice || item.average_price || item.avg_price || item.buy_price || 0) : 0;
                        const investedValue = typeof item === 'object'
                          ? Number(item.invested_amount || item.investment_amount || 0) || (averagePrice * quantity)
                          : 0;

                        // DB column is 'symboltoken' for the token used in live price lookup
                        const token = typeof item === 'object' ? (item.symboltoken || item.token || item.script_id || matchedScript?.token) : null;

                        let ltp = 0;
                        if (token && prices[token]?.price) {
                          ltp = prices[token].price;
                        } else if (typeof item === 'object' && (item.ltp || item.close)) {
                          ltp = Number(item.ltp || item.close);
                        } else if (matchedScript?.ltp || matchedScript?.Close) {
                          ltp = Number(matchedScript.ltp || matchedScript.Close);
                        }

                        const currentValue = ltp > 0 ? ltp * quantity : 0;
                        const gainLoss = (currentValue > 0 && investedValue > 0) ? currentValue - investedValue : 0;
                        const isGain = gainLoss >= 0;

                        // Broker name from broker_master via broker_id
                        const brokerId = typeof item === 'object' ? item.broker_id : null;
                        const brokerName = brokerId ? (brokersMap[brokerId] || `Broker #${brokerId}`) : 'Manual';

                        // Invested date from created_at (purchase date)
                        const investedDate = typeof item === 'object' ? (item.created_at || item.purchase_date || item.purchaseDate) : null;
                        const formattedDate = investedDate ? formatISTDate(investedDate) : '—';

                        return (
                          <tr key={idx}>
                            <td>{name}</td>
                            <td>{brokerName}</td>
                            <td>{formattedDate}</td>
                            <td>{quantity}</td>
                            <td>{ltp ? Number(ltp).toFixed(2) : '—'}</td>
                            <td>{investedValue ? Number(investedValue).toFixed(2) : '—'}</td>
                            <td>{currentValue ? Number(currentValue).toFixed(2) : '—'}</td>
                            <td className={isGain ? 'ud-gain' : 'ud-loss'}>
                              {(currentValue > 0 && investedValue > 0) ? (
                                <>{isGain ? '+' : ''}{Number(gainLoss).toFixed(2)}</>
                              ) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {portfolio.length > itemsPerPage && (
                    <div className="ud-pagination">
                      <button
                        onClick={() => setPortfolioPage(prev => Math.max(prev - 1, 1))}
                        disabled={portfolioPage === 1}
                      >
                        Previous
                      </button>
                      <span>Page {portfolioPage} of {Math.ceil(portfolio.length / itemsPerPage)}</span>
                      <button
                        onClick={() => setPortfolioPage(prev => Math.min(prev + 1, Math.ceil(portfolio.length / itemsPerPage)))}
                        disabled={portfolioPage === Math.ceil(portfolio.length / itemsPerPage)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="ud-no-data">No portfolio items</div>
              )}
            </Section>

            {/* ─── Technical / Device Info ───── */}
            <Section title="Technical Information" icon="devices" iconColor="gray">
              <div className="ud-detail-grid">
                <DetailItem label="Signup IP" value={user.signupip} mono />
                <DetailItem label="Signup Device ID" value={user.signupdeviceid} mono />
                <DetailItem label="Last Login IP" value={user.lastloginip} mono />
                <DetailItem label="Last Login Device" value={user.lastlogindevice} mono />
                <DetailItem label="App Version" value={user.appversion} />
                <DetailItem label="Platform" value={user.platform} />
              </div>
            </Section>

            {/* ─── Account Dates ────────────── */}
            <Section title="Account Timeline" icon="schedule" iconColor="green">
              <div className="ud-detail-grid">
                <DetailItem label="Created At" value={formatISTDateTime(user.createdat || user.created_at)} />
                <DetailItem label="Last Updated" value={formatISTDateTime(user.updatedat || user.updated_at)} />
                <DetailItem label="Last Login" value={formatISTDateTime(user.lastlogin || user.last_login)} />
                <DetailItem label="Registration Source" value={user.registrationsource || user.source} />
              </div>
            </Section>

          </div>
        </>
      )}
    </div>
  );
};

export default UserDetailsPage;
